import type { TranslationCatalog } from "../types.js";

function coinWord(n: number): string {
  return n === 1 ? "coin" : "coins";
}

export default {
  "common.loading": "Connecting...",
  "common.loadingEllipsis": "Loading...",
  "common.unknownError": "Unknown error",
  "common.errorPrefix": (p: { message: string }) => `Error: ${p.message}`,
  "common.retry": "Try again",
  "common.close": "Close",
  "common.back": "← Back",
  "common.cancel": "Cancel",
  "common.details": "Details",
  "common.settingsTitle": "⚙️ Settings",
  "common.profile": "👤 Profile",
  "common.rules": "📖 Rules",

  "login.subtitle": "Sign in to play.",
  "login.continueFacebook": "Continue with Facebook",
  "login.continueGoogle": "Continue with Google",
  "login.continueEmail": "Continue with email",
  "login.tagline": "A three-player trick-taking card game with bidding. Bid, pick trumps and take tricks — first to 101 points wins.",
  "login.linkRules": "Rules",
  "login.linkStrategy": "Strategy",
  "login.linkAbout": "About",
  "login.linkPrivacy": "Privacy",

  "lobby.entryFee": (p: { fee: number }) => `Entry fee: ${p.fee} ${coinWord(p.fee)}`,
  "lobby.resumeMatch": "Resume match",
  "lobby.playButton": "Play (3 players)",
  "lobby.inviteFriend": "📣 Invite a friend",
  "lobby.insufficientFunds": (p: { missing: number }) => `You don't have enough coins for the entry fee (${p.missing} short).`,
  "lobby.watchAd": "Watch an ad for more coins",

  "leaderboard.title": "🏆 Leaderboard",
  "leaderboard.emptySeason": "No ranked players this season yet (play at least 1 match).",
  "leaderboard.emptyGlobal": "No ranked players yet (play at least 1 match overall).",
  "leaderboard.seasonTabDefault": "Season",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Season ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 All-time (all seasons)",
  "leaderboard.colPlayer": "Player",
  "leaderboard.colPoints": "Points",
  "leaderboard.colMatches": "Matches",
  "leaderboard.you": "You",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Your score: ${p.points} points from ${p.matchesPlayed} matches (need at least 1 match to be ranked)`,

  "rules.title": "📖 How to play Meksiko",
  "rules.cardsHeading": "Cards",
  "rules.cardsBody":
    "Played with 32 cards (7, 8, 9, 10, J, Q, K, A in all 4 suits). Card strength within a suit, from weakest: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. The trump suit beats every non-trump card, regardless of rank.",
  "rules.dealingHeading": "Dealing",
  "rules.dealingBody": 'Each of the 3 players gets 10 cards (in two rounds of 5), and 2 cards stay face-down in the "talon".',
  "rules.biddingHeading": "Bidding",
  "rules.biddingBody":
    'The first player must bid at least 5. Each following player must bid strictly higher (5, 6, 7, 8, 9, 10, or "MEKSIKO") or say "pass" — once you pass, you are permanently out of the bidding for that deal. Bidding ends once only one player remains.',
  "rules.talonHeading": "Talon and discard",
  "rules.talonBody":
    "The bidding winner opens the talon (2 cards, visible to everyone), adds them to their hand (now 12 cards), then discards 2 cards face-down. They then choose the trump suit.",
  "rules.mexicoHeading": "MEKSIKO (20)",
  "rules.mexicoBody":
    'Bidding "MEKSIKO" commits you to winning all 10 tricks. Played without a trump suit and without the talon — just the original 10 cards.',
  "rules.tricksHeading": "Gameplay (tricks)",
  "rules.tricksBody":
    "You must follow the led suit if you can. If you can't, you must play a trump if you have one (cutting) — only if you have neither can you play anything. The trick is won by the strongest card of the led suit, unless someone played a trump — then the strongest trump wins, regardless of suit. Whoever wins the trick leads the next one.",
  "rules.scoringHeading": "Scoring",
  "rules.scoringBody":
    "The bidding winner: if they take at least as many tricks as they bid, they score that many points. If they fail, they score <strong>negative</strong> points equal to their bid. The other two players always score points equal to the number of tricks they took.",
  "rules.matchEndHeading": "End of match",
  "rules.matchEndBody":
    "The match ends once someone reaches the target score. If multiple players cross the target in the same deal with the same highest score, an extra deal is played until it's resolved.",

  "settings.soundOff": "🔇 Sound off",
  "settings.soundOn": "🔊 Sound on",
  "settings.displayNameLabel": "In-game name",
  "settings.saveDisplayName": "Save name",
  "settings.displayNameLengthError": "Name must be 1-20 characters.",
  "settings.saveFailed": "Save failed, try again.",
  "settings.logout": "🚪 Log out",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Waiting for players... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `${p.remainingSec}s left before AI fills the empty seats`,

  "game.passLabel": "pass",
  "game.passButton": "Pass",
  "game.mexicoLabel": "MEKSIKO",
  "game.bidLabel": (p: { bid: string }) => `Bid: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Trump: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Tricks: ${p.count}`,
  "game.biddingInProgress": "Bidding in progress...",
  "game.yourBid": "Your bid",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} is discarding...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} is choosing trump...`,
  "game.chooseTrumpPrompt": "Choose trump",
  "game.nextDealPause": "Next deal...",
  "game.fromTalon": "from talon",
  "game.talonRevealLabel": (p: { bidder: string }) => `Talon cards (${p.bidder})`,
  "game.yourTurnLabel": "Your turn",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}s`,
  "game.idleBotTakeover": "🤖 You missed your turn, so the AI is playing for you right now. Click anywhere to take back control.",
  "game.leaveMatch": "Leave match",
  "game.cardAlt": "card",
  "game.botDisplayName": "AI player",
  "game.botBadgeIdle": "🤖 AI (temporary)",
  "game.botBadgeFull": "🤖 BOT",
  "game.declarerTooltip": "Leading the bid",

  "matchEnd.title": "Match over",
  "matchEnd.winner": (p: { name: string }) => `Winner: ${p.name}`,
  "matchEnd.backToLobby": "Back to lobby",

  "reactions.sendButton": "Send reaction",
  "reaction.angry_small_card": "Why the small card?",
  "reaction.angry_big_card": "Why the big card?",
  "reaction.bravo": "Bravo",
  "reaction.svaka_cast": "Well played",
  "reaction.cestitam_pobeda": "Congrats on the win",
  "reaction.izvini": "Sorry",
  "reaction.pa_zasto": "Why though!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Season",
  "medal.season_silver": "🥈 Season",
  "medal.season_bronze": "🥉 Season",
  "medal.first_match": "🎉 First match",
  "medal.matches_100": "💯 100 matches",
  "medal.mexico_master": "🌵 Meksiko master",
  "medal.hat_trick_3_wins": "🔥 Hat trick (3 wins)",
  "medal.tricks_1000": "🃏 1000 tricks",

  "suit.diamonds": "Diamonds",
  "suit.hearts": "Hearts",
  "suit.clubs": "Clubs",
  "suit.leaves": "Leaves",

  "profile.matchesPlayed": "Matches played",
  "profile.firstPlace": (p: { wins: number }) => `🥇 1st place (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 2nd place (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 3rd place (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Points per match: 1st ${p.win}, 2nd ${p.second}, 3rd ${p.third} pts`,
  "profile.noMedals": "No medals earned yet.",
  "profile.medalsHeading": "Medals",
  "profile.seasonHistoryHeading": "Season history",
  "profile.colSeason": "Season",
  "profile.colRank": "Rank",
  "profile.deleteAccountButton": "🗑️ Delete account",
  "profile.deleteAccountTitle": "Delete account",
  "profile.deleteAccountWarning":
    "This permanently deletes your account and all your data (match history, leaderboard entries, medals, coin balance). This action cannot be undone.",
  "profile.deleteAccountConfirm": "Yes, delete my account",
  "profile.deleteAccountFailed": "Deletion failed, try again.",
  "profile.deleteAccountActiveMatch": "You can't delete your account while in an active match. Finish or leave the match first, then try again.",

  "errors.NOT_YOUR_TURN": "It's not your turn.",
  "errors.INVALID_BID": "Invalid bid.",
  "errors.BID_TOO_LOW": "The bid must be higher than the current highest.",
  "errors.MUST_BID_FIRST": "The first player must bid at least 5, can't pass.",
  "errors.DUPLICATE_DISCARD": "You can't discard the same card twice.",
  "errors.CARD_NOT_IN_HAND": "That card isn't in your hand.",
  "errors.MUST_FOLLOW_SUIT": "You must follow suit, or cut with trump if you can't.",
  "errors.TRICK_INCOMPLETE": "The trick isn't finished yet.",
  "errors.MATCH_ENDED": "The match has already ended.",
  "errors.UNKNOWN_ACTION": "Unknown action.",
  "errors.WRONG_PHASE": "That action isn't allowed in this phase of the game.",
  "errors.NOT_BID_WINNER": "Only the bid winner can do that.",
  "errors.MATCH_NOT_ENDED": "The match hasn't ended yet.",
  "errors.invalid_session_token": "Your session expired, please sign in again.",
  "errors.room_full": "The table is full, try again in a moment.",
  "errors.insufficient_balance": "You don't have enough coins for the entry fee.",
  "errors.ad_unavailable": "Ad currently unavailable. Check your internet connection (or disable AdBlock) and try again.",
  "errors.invalid_or_early_ticket": "The ad wasn't recognized as watched. Try again.",
  "errors.daily_ad_limit_reached": "You've reached today's ad limit. Try again tomorrow.",

  "share.inviteText": "Play Meksiko with me!",
  "ad.placeholderText": "Simulated ad (standalone web demo)",
  "rotateOverlay.text": "Rotate your phone to play Meksiko",
  "languageSwitcher.ariaLabel": "Language",
} satisfies TranslationCatalog;
